import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatCurrency } from "../lib/utils";
import USDHint from "../components/USDHint";
import SmartAlertsWidget from "../components/SmartAlertsWidget";
import { AdsSection } from "../components/ads/AdsSection";
import InventoryAlerts from "../components/inventory/InventoryAlerts";
import axios from "axios";
import {
  TrendingUp,
  TrendingDown,
  Package,
  AlertTriangle,
  Users,
  ShoppingCart,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Crown,
  Clock,
  BarChart3,
  PieChart as PieChartIcon,
  RefreshCw,
  Sparkles,
  Target,
  Award,
  Zap,
  Calendar,
  Activity,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';

const API = `${window.location.origin}/api`;

export default function DashboardPage() {
  const { } = useAuth();
  const language = "ar";
  const { t } = useTranslation(language);
  const [stats, setStats] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [customerInsights, setCustomerInsights] = useState(null);
  const [inventoryHealth, setInventoryHealth] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [salesGoals, setSalesGoals] = useState(null);
  const [expiringProducts, setExpiringProducts] = useState([]);
  const [comparison, setComparison] = useState(null); // مقارنة اليوم مع الأمس
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // ✨ تحديث تلقائي (مدموج من LiveDashboard)
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    fetchAllData();
  }, []);

  // ✨ التحديث التلقائي كل 30 ثانية لما يكون مفعّل
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchAllData();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  const fetchAllData = async () => {
    setRefreshing(true);
    try {
      const [dashboardRes, topProductsRes, chartRes, customersRes, inventoryRes, financialRes, goalsRes, expiringRes, comparisonRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`),
        axios.get(`${API}/analytics/top-products?limit=5&period=month`),
        axios.get(`${API}/analytics/sales-chart?period=week`),
        axios.get(`${API}/analytics/customer-insights`),
        axios.get(`${API}/analytics/inventory-health`),
        axios.get(`${API}/analytics/financial-summary`),
        axios.get(`${API}/settings/sales-goals`),
        axios.get(`${API}/products/expiring-soon?days=30`).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/daily-comparison`).catch(() => ({ data: null }))
      ]);
      
      setStats(dashboardRes.data);
      setTopProducts(topProductsRes.data.top_products || []);
      setChartData(chartRes.data.chart_data || []);
      setCustomerInsights(customersRes.data);
      setInventoryHealth(inventoryRes.data);
      setFinancialSummary(financialRes.data);
      setSalesGoals(goalsRes.data);
      setExpiringProducts(expiringRes.data || []);
      setComparison(comparisonRes.data);
      
      // 🔔 إشعار المنتجات منتهية الصلاحية
      const expiring = expiringRes.data || [];
      if (expiring.length > 0) {
        const expired = expiring.filter(p => {
          const expDate = new Date(p.expiry_date);
          return expDate <= new Date();
        });
        const soonExpiring = expiring.filter(p => {
          const expDate = new Date(p.expiry_date);
          const today = new Date();
          const weekLater = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
          return expDate > today && expDate <= weekLater;
        });
        
        if (expired.length > 0) {
          toast.error(
            `⚠️ ${expired.length} ${language === "ar" ? "منتج منتهي الصلاحية!" : "products expired!"}`,
            { duration: 5000 }
          );
        } else if (soonExpiring.length > 0) {
          toast.warning && toast.warning(
            `📅 ${soonExpiring.length} ${language === "ar" ? "منتج ينتهي خلال أسبوع" : "products expiring this week"}`,
            { duration: 4000 }
          );
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLastUpdate(new Date()); // ✨ تحديث وقت آخر تحديث
    }
  };

  const isArabic = language === "ar";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-600/30 border-t-emerald-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-400">{t("loading")}</p>
        </div>
      </div>
    );
  }

  // حساب أعلى قيمة للرسم البياني
  const maxSales = Math.max(...chartData.map(d => d.sales), 1);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <BarChart3 className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {isArabic ? "لوحة التحكم" : "Dashboard"}
            </h1>
            <p className="text-slate-400 text-sm">
              {isArabic ? "نظرة عامة على الأداء" : "Performance Overview"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* ✨ زر التحديث التلقائي (مدموج من المتابعة المباشرة) */}
          <Button
            onClick={() => setAutoRefresh(!autoRefresh)}
            variant={autoRefresh ? "default" : "outline"}
            className={autoRefresh ? "bg-emerald-600 hover:bg-emerald-700" : "border-slate-700 text-slate-300"}
            data-testid="auto-refresh-toggle"
            title={isArabic ? "تحديث تلقائي كل 30 ثانية" : "Auto-refresh every 30s"}
          >
            <Activity className={`w-4 h-4 me-2 ${autoRefresh ? 'animate-pulse' : ''}`} />
            {isArabic ? (autoRefresh ? "تحديث تلقائي ✓" : "تحديث تلقائي") : (autoRefresh ? "Auto ✓" : "Auto")}
          </Button>
          <Button 
            onClick={fetchAllData} 
            disabled={refreshing}
            className="bg-slate-800 hover:bg-slate-700"
          >
            <RefreshCw className={`w-4 h-4 me-2 ${refreshing ? 'animate-spin' : ''}`} />
            {isArabic ? "تحديث" : "Refresh"}
          </Button>
        </div>
      </div>

      {/* وقت آخر تحديث (للتحديث التلقائي) */}
      {autoRefresh && (
        <div className="text-xs text-slate-500 -mt-2">
          {isArabic ? "آخر تحديث: " : "Last updated: "}
          {lastUpdate.toLocaleTimeString(isArabic ? "ar-IQ" : "en-US")}
        </div>
      )}

      {/* Smart Alerts (anomaly detection) */}
      <SmartAlertsWidget />

      {/* Ads Section */}
      <AdsSection />

      {/* Inventory Alerts */}
      <InventoryAlerts compact={true} />

      {/* 📊 مقارنة اليوم مع الأمس */}
      {comparison && (
        <Card className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              <h3 className="text-white font-bold">
                {isArabic ? "مقارنة اليوم مع الأمس" : "Today vs Yesterday"}
              </h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {/* المبيعات */}
              <div className="text-center p-3 rounded-lg bg-slate-800/50">
                <p className="text-slate-400 text-xs mb-1">{isArabic ? "المبيعات" : "Sales"}</p>
                <p className="text-lg font-bold text-white">{formatCurrency(comparison.today?.sales || 0)}</p>
                <div className={`text-xs mt-1 flex items-center justify-center gap-1 ${
                  comparison.comparison?.sales?.diff >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {comparison.comparison?.sales?.diff >= 0 ? (
                    <ArrowUpRight className="w-3 h-3" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3" />
                  )}
                  {comparison.comparison?.sales?.percent || 0}%
                </div>
              </div>
              {/* عدد العمليات */}
              <div className="text-center p-3 rounded-lg bg-slate-800/50">
                <p className="text-slate-400 text-xs mb-1">{isArabic ? "العمليات" : "Transactions"}</p>
                <p className="text-lg font-bold text-white">{comparison.today?.count || 0}</p>
                <div className={`text-xs mt-1 flex items-center justify-center gap-1 ${
                  comparison.comparison?.count?.diff >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {comparison.comparison?.count?.diff >= 0 ? (
                    <ArrowUpRight className="w-3 h-3" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3" />
                  )}
                  {comparison.comparison?.count?.percent || 0}%
                </div>
              </div>
              {/* عدد القطع */}
              <div className="text-center p-3 rounded-lg bg-slate-800/50">
                <p className="text-slate-400 text-xs mb-1">{isArabic ? "القطع المباعة" : "Items Sold"}</p>
                <p className="text-lg font-bold text-white">{comparison.today?.items || 0}</p>
                <div className={`text-xs mt-1 flex items-center justify-center gap-1 ${
                  comparison.comparison?.items?.diff >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {comparison.comparison?.items?.diff >= 0 ? (
                    <ArrowUpRight className="w-3 h-3" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3" />
                  )}
                  {comparison.comparison?.items?.percent || 0}%
                </div>
              </div>
            </div>
            <p className="text-slate-500 text-xs text-center mt-3">
              {isArabic ? `الأمس: ${formatCurrency(comparison.yesterday?.sales || 0)}` : `Yesterday: ${formatCurrency(comparison.yesterday?.sales || 0)}`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats - KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Today Sales */}
        <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20 hover:border-emerald-500/40 transition-all">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-xs mb-1">
                  {isArabic ? "مبيعات اليوم" : "Today's Sales"}
                </p>
                <p className="text-2xl font-bold text-white">
                  {formatCurrency(stats?.today?.sales || 0)}
                </p>
                <USDHint amount={stats?.today?.sales || 0} />
                <p className="text-emerald-400 text-xs mt-1 flex items-center gap-1">
                  <ShoppingCart className="w-3 h-3" />
                  {stats?.today?.sales_count || 0} {isArabic ? "عملية" : "transactions"}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <DollarSign className="w-5 h-5 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Month Profit */}
        <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20 hover:border-blue-500/40 transition-all">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-xs mb-1">
                  {isArabic ? "ربح الشهر" : "Month Profit"}
                </p>
                <p className={`text-2xl font-bold ${(stats?.month?.profit || 0) >= 0 ? 'text-white' : 'text-red-400'}`}>
                  {formatCurrency(stats?.month?.profit || 0)}
                </p>
                <p className="text-blue-400 text-xs mt-1 flex items-center gap-1">
                  {(stats?.month?.profit || 0) >= 0 ? (
                    <><TrendingUp className="w-3 h-3" /> {isArabic ? "ربح" : "Profit"}</>
                  ) : (
                    <><TrendingDown className="w-3 h-3" /> {isArabic ? "خسارة" : "Loss"}</>
                  )}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-blue-500/20">
                <TrendingUp className="w-5 h-5 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products */}
        <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20 hover:border-purple-500/40 transition-all">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-xs mb-1">
                  {isArabic ? "المنتجات" : "Products"}
                </p>
                <p className="text-2xl font-bold text-white">
                  {stats?.inventory?.products_count || 0}
                </p>
                <p className="text-purple-400 text-xs mt-1 flex items-center gap-1">
                  <Package className="w-3 h-3" />
                  {inventoryHealth?.summary?.healthy || 0} {isArabic ? "متوفر" : "in stock"}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Package className="w-5 h-5 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customer Debt */}
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20 hover:border-amber-500/40 transition-all">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-xs mb-1">
                  {isArabic ? "ديون العملاء" : "Customer Debts"}
                </p>
                <p className="text-2xl font-bold text-white">
                  {formatCurrency(stats?.debts?.customer_debt || 0)}
                </p>
                <USDHint amount={stats?.debts?.customer_debt || 0} />
                <p className="text-amber-400 text-xs mt-1 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {customerInsights?.summary?.total_customers || 0} {isArabic ? "عميل" : "customers"}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Users className="w-5 h-5 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales Goals Progress */}
      {salesGoals?.enabled && (salesGoals?.daily_goal > 0 || salesGoals?.monthly_goal > 0) && (
        <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-400" />
              {isArabic ? "أهداف المبيعات" : "Sales Goals"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Daily Goal */}
              {salesGoals?.daily_goal > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">{isArabic ? "هدف اليوم" : "Daily Goal"}</span>
                    <span className="text-white">
                      {formatCurrency(stats?.today?.sales || 0)} / {formatCurrency(salesGoals.daily_goal)}
                    </span>
                  </div>
                  <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        ((stats?.today?.sales || 0) / salesGoals.daily_goal) >= 1 
                          ? 'bg-gradient-to-r from-green-500 to-emerald-400' 
                          : 'bg-gradient-to-r from-purple-500 to-pink-400'
                      }`}
                      style={{ width: `${Math.min(((stats?.today?.sales || 0) / salesGoals.daily_goal) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-center">
                    {((stats?.today?.sales || 0) / salesGoals.daily_goal) >= 1 ? (
                      <span className="text-green-400 flex items-center justify-center gap-1">
                        <Award className="w-4 h-4" />
                        {isArabic ? "🎉 تم تحقيق الهدف!" : "🎉 Goal achieved!"}
                      </span>
                    ) : (
                      <span className="text-slate-400">
                        {Math.round(((stats?.today?.sales || 0) / salesGoals.daily_goal) * 100)}% - 
                        {isArabic ? " متبقي: " : " Remaining: "}
                        {formatCurrency(Math.max(salesGoals.daily_goal - (stats?.today?.sales || 0), 0))}
                      </span>
                    )}
                  </p>
                </div>
              )}
              
              {/* Monthly Goal */}
              {salesGoals?.monthly_goal > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">{isArabic ? "هدف الشهر" : "Monthly Goal"}</span>
                    <span className="text-white">
                      {formatCurrency(stats?.month?.sales || 0)} / {formatCurrency(salesGoals.monthly_goal)}
                    </span>
                  </div>
                  <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        ((stats?.month?.sales || 0) / salesGoals.monthly_goal) >= 1 
                          ? 'bg-gradient-to-r from-green-500 to-emerald-400' 
                          : 'bg-gradient-to-r from-blue-500 to-cyan-400'
                      }`}
                      style={{ width: `${Math.min(((stats?.month?.sales || 0) / salesGoals.monthly_goal) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-center">
                    {((stats?.month?.sales || 0) / salesGoals.monthly_goal) >= 1 ? (
                      <span className="text-green-400 flex items-center justify-center gap-1">
                        <Award className="w-4 h-4" />
                        {isArabic ? "🎉 تم تحقيق الهدف!" : "🎉 Goal achieved!"}
                      </span>
                    ) : (
                      <span className="text-slate-400">
                        {Math.round(((stats?.month?.sales || 0) / salesGoals.monthly_goal) * 100)}% - 
                        {isArabic ? " متبقي: " : " Remaining: "}
                        {formatCurrency(Math.max(salesGoals.monthly_goal - (stats?.month?.sales || 0), 0))}
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Financial Summary Section - NEW */}
      <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-emerald-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-500" />
            {isArabic ? "الملخص المالي" : "Financial Summary"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Income */}
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <ArrowUpRight className="w-5 h-5 text-emerald-500" />
                <span className="text-slate-400 text-sm">{isArabic ? "الدخل (الشهر)" : "Income (Month)"}</span>
              </div>
              <p className="text-2xl font-bold text-emerald-400">
                {formatCurrency(stats?.month?.sales || 0)}
              </p>
            </div>
            {/* Expenses */}
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <ArrowDownRight className="w-5 h-5 text-red-500" />
                <span className="text-slate-400 text-sm">{isArabic ? "المصروفات" : "Expenses"}</span>
              </div>
              <p className="text-2xl font-bold text-red-400">
                {formatCurrency(financialSummary?.month?.expenses || 0)}
              </p>
            </div>
            {/* Purchases */}
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <ShoppingCart className="w-5 h-5 text-amber-500" />
                <span className="text-slate-400 text-sm">{isArabic ? "المشتريات" : "Purchases"}</span>
              </div>
              <p className="text-2xl font-bold text-amber-400">
                {formatCurrency(financialSummary?.month?.purchases || 0)}
              </p>
            </div>
            {/* Net Profit */}
            <div className={`p-4 rounded-lg text-center ${(stats?.month?.profit || 0) >= 0 ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                {(stats?.month?.profit || 0) >= 0 ? (
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-500" />
                )}
                <span className="text-slate-400 text-sm">{isArabic ? "صافي الربح" : "Net Profit"}</span>
              </div>
              <p className={`text-2xl font-bold ${(stats?.month?.profit || 0) >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                {formatCurrency(stats?.month?.profit || 0)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inventory & Financial Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Inventory Health Pie Chart */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-purple-500" />
              {isArabic ? "حالة المخزون" : "Inventory Health"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {inventoryHealth?.summary ? (
              <div className="flex items-center justify-center gap-6">
                <div className="h-48 w-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: isArabic ? 'متوفر' : 'In Stock', value: inventoryHealth.summary.healthy || 0, color: '#10b981' },
                          { name: isArabic ? 'منخفض' : 'Low Stock', value: inventoryHealth.summary.low_stock || 0, color: '#f59e0b' },
                          { name: isArabic ? 'نفد' : 'Out of Stock', value: inventoryHealth.summary.out_of_stock || 0, color: '#ef4444' },
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {[
                          { name: isArabic ? 'متوفر' : 'In Stock', value: inventoryHealth.summary.healthy || 0, color: '#10b981' },
                          { name: isArabic ? 'منخفض' : 'Low Stock', value: inventoryHealth.summary.low_stock || 0, color: '#f59e0b' },
                          { name: isArabic ? 'نفد' : 'Out of Stock', value: inventoryHealth.summary.out_of_stock || 0, color: '#ef4444' },
                        ].filter(item => item.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value, name) => [value, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-slate-400 text-sm">{isArabic ? 'متوفر:' : 'In Stock:'}</span>
                    <span className="text-white font-bold">{inventoryHealth.summary.healthy || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <span className="text-slate-400 text-sm">{isArabic ? 'منخفض:' : 'Low:'}</span>
                    <span className="text-white font-bold">{inventoryHealth.summary.low_stock || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-slate-400 text-sm">{isArabic ? 'نفد:' : 'Out:'}</span>
                    <span className="text-white font-bold">{inventoryHealth.summary.out_of_stock || 0}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                {isArabic ? "لا توجد بيانات" : "No data"}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Financial Distribution Pie Chart */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              {isArabic ? "توزيع المصروفات" : "Expense Distribution"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.month?.sales > 0 || financialSummary?.month?.expenses > 0) ? (
              <div className="flex items-center justify-center gap-6">
                <div className="h-48 w-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: isArabic ? 'المبيعات' : 'Sales', value: stats?.month?.sales || 0, color: '#10b981' },
                          { name: isArabic ? 'المصروفات' : 'Expenses', value: financialSummary?.month?.expenses || 0, color: '#ef4444' },
                          { name: isArabic ? 'المشتريات' : 'Purchases', value: financialSummary?.month?.purchases || 0, color: '#f59e0b' },
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {[
                          { name: isArabic ? 'المبيعات' : 'Sales', value: stats?.month?.sales || 0, color: '#10b981' },
                          { name: isArabic ? 'المصروفات' : 'Expenses', value: financialSummary?.month?.expenses || 0, color: '#ef4444' },
                          { name: isArabic ? 'المشتريات' : 'Purchases', value: financialSummary?.month?.purchases || 0, color: '#f59e0b' },
                        ].filter(item => item.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value) => [formatCurrency(value)]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-slate-400 text-sm">{isArabic ? 'المبيعات:' : 'Sales:'}</span>
                    <span className="text-emerald-400 font-bold">{formatCurrency(stats?.month?.sales || 0)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-slate-400 text-sm">{isArabic ? 'المصروفات:' : 'Expenses:'}</span>
                    <span className="text-red-400 font-bold">{formatCurrency(financialSummary?.month?.expenses || 0)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <span className="text-slate-400 text-sm">{isArabic ? 'المشتريات:' : 'Purchases:'}</span>
                    <span className="text-amber-400 font-bold">{formatCurrency(financialSummary?.month?.purchases || 0)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                {isArabic ? "لا توجد بيانات" : "No data"}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Expiring Products Alert - NEW */}
      {expiringProducts.length > 0 && (
        <Card className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border-red-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 animate-pulse" />
              {isArabic ? `⚠️ منتجات قريبة الانتهاء (${expiringProducts.length})` : `⚠️ Expiring Products (${expiringProducts.length})`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {expiringProducts.slice(0, 6).map((product, index) => {
                const daysLeft = Math.ceil((new Date(product.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
                const isExpired = daysLeft < 0;
                return (
                  <div 
                    key={index} 
                    className={`p-3 rounded-lg border ${isExpired ? 'bg-red-500/20 border-red-500/50' : 'bg-orange-500/10 border-orange-500/30'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white font-medium text-sm">{product.name}</p>
                        <p className="text-slate-400 text-xs">{isArabic ? "الكمية:" : "Qty:"} {product.quantity}</p>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs font-bold ${isExpired ? 'bg-red-500 text-white' : 'bg-orange-500 text-white'}`}>
                        {isExpired 
                          ? (isArabic ? "منتهي!" : "Expired!") 
                          : (isArabic ? `${daysLeft} يوم` : `${daysLeft} days`)}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {isArabic ? "تاريخ الانتهاء:" : "Expires:"} {new Date(product.expiry_date).toLocaleDateString('ar-IQ')}
                    </p>
                  </div>
                );
              })}
            </div>
            {expiringProducts.length > 6 && (
              <p className="text-center text-slate-400 text-sm mt-3">
                {isArabic ? `و ${expiringProducts.length - 6} منتج آخر...` : `and ${expiringProducts.length - 6} more...`}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Customer Debts Summary - NEW */}
      {(stats?.debts?.customer_debt > 0 || customerInsights?.summary?.customers_with_debt > 0) && (
        <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-500" />
              {isArabic ? "إحصائيات العملاء والديون" : "Customer & Debt Statistics"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-slate-800/50 text-center">
                <p className="text-slate-400 text-xs mb-1">{isArabic ? "إجمالي العملاء" : "Total Customers"}</p>
                <p className="text-xl font-bold text-white">{customerInsights?.summary?.total_customers || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 text-center">
                <p className="text-slate-400 text-xs mb-1">{isArabic ? "عملاء عليهم ديون" : "Customers with Debt"}</p>
                <p className="text-xl font-bold text-amber-400">{customerInsights?.summary?.customers_with_debt || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 text-center">
                <p className="text-slate-400 text-xs mb-1">{isArabic ? "إجمالي الديون" : "Total Debts"}</p>
                <p className="text-xl font-bold text-red-400">{formatCurrency(stats?.debts?.customer_debt || 0)}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 text-center">
                <p className="text-slate-400 text-xs mb-1">{isArabic ? "عملاء VIP" : "VIP Customers"}</p>
                <p className="text-xl font-bold text-emerald-400">{customerInsights?.summary?.vip_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sales Chart + Alerts */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Enhanced Sales Chart with Recharts */}
        <Card className="lg:col-span-2 bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-500" />
              {isArabic ? "مبيعات الأسبوع" : "Weekly Sales"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis 
                    dataKey="day" 
                    stroke="#94a3b8" 
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    axisLine={{ stroke: '#475569' }}
                  />
                  <YAxis 
                    stroke="#94a3b8"
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    axisLine={{ stroke: '#475569' }}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1e293b', 
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    labelStyle={{ color: '#94a3b8' }}
                    formatter={(value) => [formatCurrency(value), isArabic ? 'المبيعات' : 'Sales']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="sales" 
                    stroke="#10b981" 
                    strokeWidth={3}
                    fillOpacity={1} 
                    fill="url(#salesGradient)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-between mt-4 text-xs text-slate-400">
              <span>{isArabic ? "إجمالي الأسبوع:" : "Week Total:"} {formatCurrency(chartData.reduce((sum, d) => sum + d.sales, 0))}</span>
              <span>{isArabic ? "المعاملات:" : "Transactions:"} {chartData.reduce((sum, d) => sum + d.count, 0)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {isArabic ? "التنبيهات" : "Alerts"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Low Stock Alert */}
            {inventoryHealth?.summary?.low_stock > 0 && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-amber-500" />
                  <span className="text-amber-400 text-sm font-medium">
                    {inventoryHealth.summary.low_stock} {isArabic ? "منتج نفد" : "low stock"}
                  </span>
                </div>
              </div>
            )}
            
            {/* Out of Stock Alert */}
            {inventoryHealth?.summary?.out_of_stock > 0 && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span className="text-red-400 text-sm font-medium">
                    {inventoryHealth.summary.out_of_stock} {isArabic ? "منتج غير متوفر" : "out of stock"}
                  </span>
                </div>
              </div>
            )}
            
            {/* Expiring Alert */}
            {inventoryHealth?.summary?.expiring_soon > 0 && (
              <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <span className="text-orange-400 text-sm font-medium">
                    {inventoryHealth.summary.expiring_soon} {isArabic ? "منتج ينتهي قريباً" : "expiring soon"}
                  </span>
                </div>
              </div>
            )}
            
            {/* VIP Customers */}
            {customerInsights?.summary?.vip_count > 0 && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4 text-emerald-500" />
                  <span className="text-emerald-400 text-sm font-medium">
                    {customerInsights.summary.vip_count} {isArabic ? "عميل VIP" : "VIP customers"}
                  </span>
                </div>
              </div>
            )}
            
            {/* No Alerts */}
            {(!inventoryHealth?.summary?.low_stock && 
              !inventoryHealth?.summary?.out_of_stock && 
              !inventoryHealth?.summary?.expiring_soon) && (
              <div className="p-3 rounded-lg bg-slate-700/50 text-center">
                <Sparkles className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                <span className="text-slate-400 text-sm">
                  {isArabic ? "كل شيء على ما يرام!" : "Everything looks good!"}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Products + VIP Customers */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Products with Bar Chart */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              {isArabic ? "أفضل المنتجات مبيعاً" : "Top Selling Products"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length > 0 ? (
              <>
                {/* Bar Chart for Top Products */}
                <div className="h-48 mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={topProducts.slice(0, 5).map(p => ({
                        name: p.product_name.length > 10 ? p.product_name.substring(0, 10) + '...' : p.product_name,
                        revenue: p.total_revenue,
                        quantity: p.total_quantity
                      }))} 
                      layout="vertical"
                      margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={true} vertical={false} />
                      <XAxis 
                        type="number" 
                        stroke="#94a3b8" 
                        tick={{ fill: '#94a3b8', fontSize: 10 }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        stroke="#94a3b8"
                        tick={{ fill: '#94a3b8', fontSize: 10 }}
                        width={80}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value) => [formatCurrency(value), isArabic ? 'الإيرادات' : 'Revenue']}
                      />
                      <Bar dataKey="revenue" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* Product List */}
                <div className="space-y-2">
                  {topProducts.slice(0, 3).map((product, index) => (
                    <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-slate-900/50">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? 'bg-amber-500 text-white' :
                          index === 1 ? 'bg-slate-400 text-white' :
                          'bg-amber-700 text-white'
                        }`}>
                          {index + 1}
                        </div>
                        <span className="text-white text-sm">{product.product_name}</span>
                      </div>
                      <span className="text-emerald-400 font-bold text-sm">{formatCurrency(product.total_revenue)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-slate-400">
                {isArabic ? "لا توجد مبيعات بعد" : "No sales yet"}
              </div>
            )}</CardContent>
        </Card>

        {/* VIP Customers */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              {isArabic ? "أفضل العملاء" : "Top Customers"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {customerInsights?.top_customers?.length > 0 ? (
              <div className="space-y-3">
                {customerInsights.top_customers.slice(0, 5).map((customer, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 hover:bg-slate-900 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        customer.tier === 'vip' ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-300'
                      }`}>
                        {customer.tier === 'vip' ? <Crown className="w-4 h-4" /> : (index + 1)}
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{customer.name}</p>
                        <p className="text-slate-400 text-xs">{customer.phone}</p>
                      </div>
                    </div>
                    <div className="text-end">
                      <p className="text-emerald-400 font-bold text-sm">
                        {formatCurrency(customer.total_purchases || 0)}
                      </p>
                      {customer.debt > 0 && (
                        <p className="text-red-400 text-xs">
                          {isArabic ? "دين:" : "Debt:"} {formatCurrency(customer.debt)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                {isArabic ? "لا يوجد عملاء بعد" : "No customers yet"}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-slate-400 text-xs mb-1">{isArabic ? "مبيعات الأسبوع" : "Week Sales"}</p>
            <p className="text-xl font-bold text-white">{formatCurrency(financialSummary?.week?.sales || 0)}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-slate-400 text-xs mb-1">{isArabic ? "مبيعات الشهر" : "Month Sales"}</p>
            <p className="text-xl font-bold text-white">{formatCurrency(financialSummary?.month?.sales || 0)}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-slate-400 text-xs mb-1">{isArabic ? "مبيعات السنة" : "Year Sales"}</p>
            <p className="text-xl font-bold text-white">{formatCurrency(financialSummary?.year?.sales || 0)}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-slate-400 text-xs mb-1">{isArabic ? "صافي الوضع" : "Net Position"}</p>
            <p className={`text-xl font-bold ${(financialSummary?.net_position || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {formatCurrency(financialSummary?.net_position || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* App Branding */}
      <div className="text-center pt-4 text-slate-500 text-sm">
        <div className="flex items-center justify-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="text-emerald-500 font-semibold">Nazario-Hesabaty</span>
        </div>
      </div>
    </div>
  );
}
